import { useEffect, useState } from 'react';

import etribeLogo from '../../assets/icons/etribe-blue.svg';
import etribeWhite from '../../assets/icons/etribe-white.svg';

const NAV_LINKS = [
  { href: '#nosotros',   label: 'Nosotros' },
  { href: '#servicios',  label: 'Servicios' },
  { href: '#casos',      label: 'Casos de éxito' },
  { href: '#industrias', label: 'Industrias' },
  { href: '#partners',   label: 'Partners' },
];

export default function Header() {
  // Inicializa con el scroll real para evitar flash de estado incorrecto en refresh mid-page.
  const [scrolled, setScrolled] = useState(
    () => typeof window !== 'undefined' && window.scrollY > 24,
  );
  const [open, setOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('');

  useEffect(() => {
    let rafId = 0;
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        setScrolled(window.scrollY > 24);

        // ScrollSpy logic
        const sectionIds = NAV_LINKS.map((link) => link.href.replace('#', ''));
        if (window.scrollY < 100) {
          setActiveSection('');
        } else {
          let currentActive = '';
          const targetY = window.scrollY + 120; // 120px offset from top of viewport

          for (const id of sectionIds) {
            const el = document.getElementById(id);
            if (el) {
              const top = window.scrollY + el.getBoundingClientRect().top;
              const bottom = top + el.offsetHeight;
              if (targetY >= top && targetY < bottom) {
                currentActive = id;
                break;
              }
            }
          }
          if (currentActive) {
            setActiveSection(currentActive);
          }
        }

        rafId = 0;
      });
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    // Run once on mount to set initial state
    onScroll();

    return () => {
      window.removeEventListener('scroll', onScroll);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, []);

  return (
    <header
      className={[
        'fixed top-0 inset-x-0 z-50 transition-all duration-300 border-b',
        scrolled
          ? 'bg-white/95 backdrop-blur-md border-etribe-line shadow-sm'
          : 'bg-transparent border-transparent',
      ].join(' ')}
    >
      <div className="container-etribe flex items-center justify-between h-[76px] gap-8">
        <a
          href="#top"
          aria-label="eTribe — Inicio"
          className="inline-flex items-center transition-opacity hover:opacity-80"
        >
          <img
            src={scrolled ? etribeWhite : etribeLogo}
            alt="eTribe"
            className="h-10 w-auto select-none"
            draggable={false}
          />
        </a>

        <nav className="hidden lg:flex items-center gap-8" aria-label="Principal">
          {NAV_LINKS.map(({ href, label }) => {
            const isActive = activeSection === href.replace('#', '');
            return (
              <a
                key={href}
                href={href}
                className={[
                  'text-sm font-medium transition-colors relative py-1.5',
                  isActive
                    ? scrolled
                      ? 'text-etribe-blue font-semibold'
                      : 'text-white font-semibold'
                    : scrolled
                    ? 'text-etribe-ink-2 hover:text-etribe-blue'
                    : 'text-white/85 hover:text-white',
                ].join(' ')}
              >
                {label}
                <span
                  className={[
                    'absolute bottom-0 inset-x-0 h-0.5 bg-etribe-yellow transition-all duration-300 origin-center',
                    isActive ? 'scale-x-100 opacity-100' : 'scale-x-0 opacity-0',
                  ].join(' ')}
                />
              </a>
            );
          })}
        </nav>

        <div className="flex items-center gap-4">
          <a
            href="#contacto"
            className="bg-etribe-yellow text-etribe-blue font-semibold text-sm px-5 py-2.5 rounded-[10px] hover:shadow-[0_6px_18px_rgba(255,200,54,.45)] hover:-translate-y-0.5 transition-all"
          >
            Hablemos
          </a>
          <button
            type="button"
            aria-label="Abrir menú"
            aria-expanded={open}
            onClick={() => setOpen((v) => !v)}
            className={[
              'lg:hidden inline-flex w-10 h-10 items-center justify-center transition-colors',
              scrolled ? 'text-etribe-ink' : 'text-white',
            ].join(' ')}
          >
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" strokeLinecap="round">
              <path d="M4 7h16M4 12h16M4 17h16" />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <nav
          className="lg:hidden bg-white/95 backdrop-blur-md border-t border-etribe-line"
          aria-label="Principal móvil"
        >
          <div className="container-etribe py-4 flex flex-col gap-1">
            {NAV_LINKS.map(({ href, label }) => {
              const isActive = activeSection === href.replace('#', '');
              return (
                <a
                  key={href}
                  href={href}
                  onClick={() => setOpen(false)}
                  className={[
                    'py-3 rounded-md text-sm font-medium transition-all flex items-center justify-between',
                    isActive
                      ? 'text-etribe-blue bg-etribe-blue/[0.03] border-l-4 border-etribe-yellow pl-2 pr-3'
                      : 'px-3 text-etribe-ink-2 hover:bg-etribe-gray-bg hover:text-etribe-blue',
                  ].join(' ')}
                >
                  {label}
                </a>
              );
            })}
          </div>
        </nav>
      )}
    </header>
  );
}
